import { useEffect, useId, useLayoutEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Layers, ShieldCheck } from 'lucide-react'
import { updateRole } from '@/api/services/roles.service'
import type { Role, UpdateRolePayload } from '@/types'
import { Button, Input, useModalFooterSlot } from '@/components/ui'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type { AxiosError } from 'axios'
import { rolesQueryKeys } from '../hooks/useRoles'

const updateRoleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  description: z.string().optional(),
})

type UpdateRoleFormValues = z.infer<typeof updateRoleSchema>

function scopeBadgeText(scope: Role['scope']): string {
  if (scope === 'all') return 'Org-wide'
  if (scope === 'company' || scope === 'team') return 'Company-scoped'
  return 'Personal'
}

export interface EditRoleFormProps {
  role: Role
  onSuccess: () => void
  onCancel: () => void
}

export function EditRoleForm({ role, onSuccess, onCancel }: EditRoleFormProps): React.ReactElement {
  const formId = useId()
  const setModalFooter = useModalFooterSlot()
  const queryClient = useQueryClient()

  // System roles are locked on the backend (only `name` and `description` can change).
  const isSystem = role.isSystemRole

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateRoleFormValues>({
    resolver: zodResolver(updateRoleSchema),
    defaultValues: {
      name: role.name,
      slug: role.slug,
      description: role.description ?? '',
    },
  })

  useEffect(() => {
    reset({
      name: role.name,
      slug: role.slug,
      description: role.description ?? '',
    })
  }, [role, reset])

  const mutation = useMutation({
    mutationFn: (payload: UpdateRolePayload) => updateRole(role._id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.all })
      // Role name/scope render on users and the current session's permissions.
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
      toast.success('Role updated')
      onSuccess()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const onSubmit = (values: UpdateRoleFormValues): void => {
    // For system roles, only name + description are accepted by the backend.
    const payload: UpdateRolePayload = isSystem
      ? {
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
        }
      : {
          name: values.name.trim(),
          slug: values.slug.trim(),
          description: values.description?.trim() || undefined,
        }
    mutation.mutate(payload)
  }

  const apiError =
    mutation.error instanceof Error
      ? mutation.error.message
      : mutation.error && typeof mutation.error === 'object' && 'response' in mutation.error
        ? (mutation.error as AxiosError<{ message?: string }>).response?.data?.message ?? null
        : null

  useLayoutEffect(() => {
    if (!setModalFooter) return
    setModalFooter(
      <>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button form={formId} type="submit" loading={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </>
    )
    return () => setModalFooter(null)
  }, [setModalFooter, onCancel, mutation.isPending, formId])

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {apiError && <p className="text-sm text-danger">{apiError}</p>}

      {isSystem && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/50 dark:bg-amber-900/20">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            This is a built-in system role. Only the display name and description can be edited.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          <Layers className="h-3 w-3" aria-hidden />
          {scopeBadgeText(role.scope)}
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          Scope is set by the role’s template and cannot be changed here.
        </span>
      </div>

      <Input
        label="Name"
        {...register('name')}
        error={errors.name?.message}
        placeholder="e.g. Team Lead"
      />
      <Input
        label="Slug"
        {...register('slug')}
        error={errors.slug?.message}
        placeholder="e.g. team-lead"
        disabled={isSystem}
      />
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Description
        </label>
        <textarea
          {...register('description')}
          rows={2}
          className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          placeholder="Optional description"
        />
      </div>

      {!setModalFooter && (
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </form>
  )
}

EditRoleForm.displayName = 'EditRoleForm'
